import {messages} from '@smartmixin/i18n';
import {CaretDown} from '@phosphor-icons/react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Link,
  Typography,
} from '@mui/material';
import {useMemo} from 'react';
import {useIntl} from 'react-intl';
import {useScrollSpy} from '../hooks/useScrollSpy';
import {scrollToHeading, TocHeading} from '../utils/headings';

type TableOfContentsProps = {
  headings: TocHeading[];
  // 'rail' is the sticky desktop sidebar; 'inline' is the collapsible
  // disclosure shown above the article on smaller screens.
  variant?: 'rail' | 'inline';
};

type TocLinksProps = {
  headings: TocHeading[];
  activeId: string | null;
};

const TocLinks = ({headings, activeId}: TocLinksProps) => (
  <Box component="ul" sx={{listStyle: 'none', m: 0, p: 0}}>
    {headings.map((heading, index) => {
      const active = heading.id === activeId;
      return (
        <Box component="li" key={`${heading.id}-${index}`}>
          <Link
            href={`#${heading.id}`}
            underline="none"
            // The section in view, for a screen reader too (it was colour
            // and weight only).
            aria-current={active ? 'location' : undefined}
            onClick={(event) => {
              event.preventDefault();
              scrollToHeading(heading.id);
            }}
            sx={{
              display: 'block',
              py: 0.5,
              pl: heading.level === 3 ? 3 : 1.5,
              borderLeft: 2,
              borderColor: active ? 'ink.main' : 'divider',
              color: active ? 'ink.main' : 'text.secondary',
              typography: 'data',
              fontWeight: active ? 'fontWeightMedium' : 'fontWeightRegular',
              transition: 'color 150ms ease, border-color 150ms ease',
              '&:hover': {color: 'text.primary'},
            }}>
            {heading.text}
          </Link>
        </Box>
      );
    })}
  </Box>
);

const TableOfContents = ({
  headings,
  variant = 'rail',
}: TableOfContentsProps) => {
  const {formatMessage} = useIntl();
  const ids = useMemo(() => headings.map((heading) => heading.id), [headings]);
  const activeId = useScrollSpy(ids);
  const onThisPageLabel = formatMessage(messages.helpCenter.onThisPage);

  if (headings.length === 0) {
    return null;
  }

  if (variant === 'inline') {
    return (
      <Accordion
        disableGutters
        elevation={0}
        sx={{
          mb: 3,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          backgroundColor: 'background.default',
          '&:before': {display: 'none'},
        }}>
        <AccordionSummary expandIcon={<CaretDown aria-hidden size={16} />}>
          <Typography variant="title" component="span">
            {onThisPageLabel}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{pt: 0}}>
          <TocLinks headings={headings} activeId={activeId} />
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <Box component="nav" aria-label={onThisPageLabel}>
      <Typography
        variant="caption"
        component="p"
        sx={{
          display: 'block',
          mb: 1,
          pl: 1.5,
          color: 'text.secondary',
          fontWeight: 'fontWeightMedium',
        }}>
        {onThisPageLabel}
      </Typography>
      <TocLinks headings={headings} activeId={activeId} />
    </Box>
  );
};

export default TableOfContents;
